import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.jsx";
import { acksApi } from "../api/client.js";
import { CheckInHistoryModal } from "../components/CheckInHistoryModal.jsx";
import { lastWeekdays, weekdaysBetween, ymd } from "../lib/domain.js";

/* Your own account: who the board thinks you are. There is nothing here to
   change — sign-in is your Google account, and access, role and the daily
   flag are an administrator's to set — so the page says so rather than
   showing controls that would be refused. */
export function AccountPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [history, setHistory] = useState(null);
  const [reading, setReading] = useState(false);
  const [failed, setFailed] = useState("");

  /* Your own record, fetched when you ask for it rather than on every visit
     to this page — most of the time you are here to read the facts above. */
  async function openHistory() {
    if (history) return;
    setReading(true);
    setFailed("");
    try {
      const today = ymd(new Date());
      const start = user.joinedAt ? ymd(new Date(user.joinedAt)) : null;
      const days = start ? weekdaysBetween(start, today) : lastWeekdays(14);
      const { history: record } = await acksApi.historyRange(days[0] || today, today);
      setHistory({ days, record });
    } catch (e) {
      setFailed(e.message);
    } finally {
      setReading(false);
    }
  }

  return (
    <div className="page narrow">
      <div className="pagehead">
        <div>
          <h2 className="pagetitle">Your account</h2>
          <p className="pagesub mono">{user.email}</p>
        </div>
        <Link className="btn btn-sm" to="/">
          Back to the board
        </Link>
      </div>

      <div className="sechead">
        <span className="label">What the board knows</span>
        <span className="rule" />
      </div>

      <dl className="facts">
        <dt>Name</dt>
        <dd>{user.name || <i>not recorded</i>}</dd>
        <dt>Role in the org</dt>
        <dd>{user.title || <i>not recorded</i>}</dd>
        <dt>Permission</dt>
        <dd>{user.role === "admin" ? "Administrator — can change who has access" : "Member"}</dd>
        <dt>Daily reviewer</dt>
        <dd>{user.daily ? "Yes — expected to work the board each day" : "No"}</dd>
        <dt>Sign-in</dt>
        <dd>Google{user.avatarUrl ? " · account linked" : ""}</dd>
        <dt>Allowed by</dt>
        <dd>
          {user.joinedVia ? (
            <>
              the rule <span className="mono">{user.joinedVia}</span>
            </>
          ) : (
            "being named on the access list"
          )}
        </dd>
      </dl>

      <p className="hintline">
        Your name, role and daily flag are set under Access by an
        administrator, so the core group stays one list. There is no password
        to change — this War Room signs in with Google only.
      </p>

      <div className="sechead" style={{ paddingTop: "20px" }}>
        <span className="label">Your daily check-back</span>
        <span className="rule" />
      </div>

      {user.daily ? (
        <>
          <p className="hintline">
            Every weekday since you joined, acknowledged or missed, read from the
            shared record.
          </p>
          <button className="btn" type="button" onClick={openHistory} disabled={reading}>
            {reading ? "Reading the record…" : "Your check-in history"}
          </button>
          {failed && (
            <div className="notice">
              <b>Could not read the record.</b> {failed}
            </div>
          )}
        </>
      ) : (
        <p className="hintline">
          You are not marked as a daily reviewer, so there is no check-back
          record to show.
        </p>
      )}

      <div className="sechead" style={{ paddingTop: "20px" }}>
        <span className="label">This session</span>
        <span className="rule" />
      </div>
      <button
        className="btn"
        type="button"
        onClick={async () => {
          await signOut();
          navigate("/login", { replace: true });
        }}
      >
        Sign out
      </button>

      {history && (
        <CheckInHistoryModal
          person={user}
          days={history.days}
          history={history.record}
          initialTab="all"
          onClose={() => setHistory(null)}
        />
      )}
    </div>
  );
}
